/** Filter operators exposed in the query builder UI. */
export const FILTER_OPERATORS = [
  { id: 'eq', label: 'Equals' },
  { id: 'neq', label: 'Not equals' },
  { id: 'contains', label: 'Contains' },
  { id: 'gt', label: 'Greater than' },
  { id: 'lt', label: 'Less than' },
  { id: 'in', label: 'In (comma-separated)' },
];

/** Time bucketing when a date field is used as a dimension. */
export const TIME_GRAINS = [
  { id: 'day', label: 'By day' },
  { id: 'week', label: 'By week' },
  { id: 'month', label: 'By month' },
];

/** Metric aggregations supported client-side. */
export const METRIC_TYPES = [
  { id: 'count', label: 'Count' },
  { id: 'sum', label: 'Sum' },
  { id: 'avg', label: 'Average' },
];

export const VISUALIZATION_TYPES = [
  { id: 'table', label: 'Data table', dragType: 'viz-table' },
  { id: 'line', label: 'Line chart', dragType: 'viz-line' },
  { id: 'bar', label: 'Bar chart', dragType: 'viz-bar' },
];

/**
 * Company-scoped report data sources.
 * `collectionPath` is either a top-level collection id or `companies/{companyId}/sub`.
 */
export const REPORT_DATA_SOURCES = [
  {
    id: 'tickets',
    label: 'Tickets',
    module: 'ticketing',
    collectionPath: 'tickets',
    scope: 'top-level',
    companyField: 'companyId',
    appField: 'appId',
    fields: [
      { id: 'status', label: 'Status', type: 'string' },
      { id: 'priority', label: 'Priority', type: 'string' },
      { id: 'appId', label: 'App / Site', type: 'string' },
      { id: 'assigneeUid', label: 'Assignee', type: 'string' },
      { id: 'reporterUid', label: 'Reporter', type: 'string' },
      { id: 'archived', label: 'Archived', type: 'boolean' },
      { id: 'isAssignedToAi', label: 'AI assigned', type: 'boolean' },
      { id: 'createdAt', label: 'Created', type: 'timestamp' },
      { id: 'updatedAt', label: 'Updated', type: 'timestamp' },
    ],
    numericFields: [],
  },
  {
    id: 'chats',
    label: 'Chat sessions',
    module: 'chatWidget',
    collectionPath: 'chats',
    scope: 'top-level',
    companyField: 'companyId',
    appField: 'appId',
    fields: [
      { id: 'status', label: 'Status', type: 'string' },
      { id: 'appId', label: 'App / Site', type: 'string' },
      { id: 'assignedAgentUid', label: 'Agent', type: 'string' },
      { id: 'visitorTimezone', label: 'Visitor timezone', type: 'string' },
      { id: 'createdAt', label: 'Created', type: 'timestamp' },
      { id: 'updatedAt', label: 'Updated', type: 'timestamp' },
    ],
    numericFields: [],
  },
  {
    id: 'crm_campaigns',
    label: 'CRM campaigns',
    module: 'crm',
    collectionPath: 'crm_campaigns',
    scope: 'company-sub',
    companyField: null,
    appField: 'appId',
    fields: [
      { id: 'status', label: 'Status', type: 'string' },
      { id: 'appId', label: 'App / Site', type: 'string' },
      { id: 'templateId', label: 'Template', type: 'string' },
      { id: 'audienceCount', label: 'Audience size', type: 'number' },
      { id: 'scheduledFor', label: 'Scheduled', type: 'timestamp' },
      { id: 'createdAt', label: 'Created', type: 'timestamp' },
    ],
    numericFields: ['audienceCount'],
  },
  {
    id: 'uptime_monitoring',
    label: 'Uptime monitoring',
    module: 'uptimeMonitoring',
    collectionPath: 'companyApps',
    scope: 'top-level',
    companyField: 'companyId',
    appField: 'appId',
    fields: [
      { id: 'name', label: 'Site name', type: 'string' },
      { id: 'liveStatus', label: 'Live status', type: 'string' },
      { id: 'url', label: 'URL', type: 'string' },
      { id: 'livenessAlertsEnabled', label: 'Alerts enabled', type: 'boolean' },
      { id: 'heartbeatAt', label: 'Last heartbeat', type: 'timestamp' },
      { id: 'createdAt', label: 'Created', type: 'timestamp' },
    ],
    numericFields: [],
  },
  {
    id: 'agile_tasks',
    label: 'Agile tasks',
    module: 'agileKanban',
    collectionPath: 'tasks',
    scope: 'top-level',
    companyField: 'companyId',
    appField: 'appId',
    fields: [
      { id: 'status', label: 'Status', type: 'string' },
      { id: 'type', label: 'Type', type: 'string' },
      { id: 'sprintId', label: 'Sprint', type: 'string' },
      { id: 'assigneeUid', label: 'Assignee', type: 'string' },
      { id: 'createdAt', label: 'Created', type: 'timestamp' },
    ],
    numericFields: [],
  },
  {
    id: 'ai_usage',
    label: 'AI usage (LLM)',
    module: 'aiSpend',
    collectionPath: 'aiUsageLog',
    scope: 'top-level',
    companyField: 'companyId',
    appField: null,
    fields: [
      { id: 'provider', label: 'Provider', type: 'string' },
      { id: 'model', label: 'Model', type: 'string' },
      { id: 'modelKey', label: 'Model key', type: 'string' },
      { id: 'source', label: 'Source', type: 'string' },
      { id: 'ticketId', label: 'Ticket', type: 'string' },
      { id: 'pricingMissing', label: 'Pricing missing', type: 'boolean' },
      { id: 'costUsd', label: 'Cost (USD)', type: 'number' },
      { id: 'input_tokens', label: 'Input tokens', type: 'number' },
      { id: 'output_tokens', label: 'Output tokens', type: 'number' },
      {
        id: 'cache_creation_input_tokens',
        label: 'Cache creation tokens',
        type: 'number',
      },
      {
        id: 'cache_read_input_tokens',
        label: 'Cache read tokens',
        type: 'number',
      },
      { id: 'createdAt', label: 'Created', type: 'timestamp' },
    ],
    numericFields: [
      'costUsd',
      'input_tokens',
      'output_tokens',
      'cache_creation_input_tokens',
      'cache_read_input_tokens',
    ],
  },
  {
    id: 'company_costs',
    label: 'Company costs',
    module: 'aiSpend',
    collectionPath: 'costEntries',
    scope: 'company-sub',
    companyField: null,
    appField: null,
    fields: [
      { id: 'category', label: 'Category', type: 'string' },
      { id: 'provider', label: 'Provider', type: 'string' },
      { id: 'planName', label: 'Plan name', type: 'string' },
      { id: 'periodMonth', label: 'Period month', type: 'string' },
      { id: 'source', label: 'Source', type: 'string' },
      { id: 'amountUsd', label: 'Amount (USD)', type: 'number' },
      { id: 'createdAt', label: 'Created', type: 'timestamp' },
      { id: 'updatedAt', label: 'Updated', type: 'timestamp' },
    ],
    numericFields: ['amountUsd'],
  },
  {
    id: 'ticket_briefings',
    label: 'Ticket briefings',
    module: 'ticketing',
    collectionPath: 'ticketBriefings',
    scope: 'top-level',
    companyField: 'companyId',
    appField: null,
    fields: [
      { id: 'disposition', label: 'Disposition', type: 'string' },
      { id: 'projectKind', label: 'Project kind', type: 'string' },
      { id: 'assigneeAgentRole', label: 'Agent role', type: 'string' },
      { id: 'resolutionPath', label: 'Resolution path', type: 'string' },
      {
        id: 'humanInterventionBeyondReview',
        label: 'Human intervention',
        type: 'boolean',
      },
      { id: 'frictionTag', label: 'Friction tag (exploratory incl. narration)', type: 'string' },
      { id: 'frictionTags', label: 'Friction tags (exploratory incl. narration)', type: 'string' },
      { id: 'ticketId', label: 'Ticket', type: 'string' },
      { id: 'aiCallCount', label: 'AI calls', type: 'number' },
      { id: 'aiSpendUsd', label: 'AI spend (USD)', type: 'number' },
      { id: 'wallClockMs', label: 'Wall clock (ms)', type: 'number' },
      { id: 'extractedAt', label: 'Extracted', type: 'timestamp' },
    ],
    numericFields: ['aiCallCount', 'aiSpendUsd', 'wallClockMs'],
  },
];

/** @param {string} sourceId */
export function getReportDataSource(sourceId) {
  return REPORT_DATA_SOURCES.find((s) => s.id === sourceId) || REPORT_DATA_SOURCES[0];
}

/** Default empty widget query configuration. */
export function createEmptyQuery(sourceId = 'tickets') {
  const source = getReportDataSource(sourceId);
  const firstDim = source.fields[0]?.id || 'status';
  return {
    dimensions: [firstDim],
    metrics: [{ type: 'count', field: '*' }],
    filters: [],
    timeGrain: null,
    appId: 'all',
  };
}

/** @returns {import('./reportWidgetUtils').ReportWidget} */
export function createWidgetFromDrop({ vizType, dataSourceId, title }) {
  const source = getReportDataSource(dataSourceId || 'tickets');
  return {
    id: `widget_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    title: title || `${source.label} report`,
    vizType: vizType || 'table',
    dataSourceId: source.id,
    viewMode: vizType === 'table' ? 'table' : 'chart',
    query: createEmptyQuery(source.id),
  };
}
